/* eslint-disable no-param-reassign */
import React from 'react';
import moment from 'moment';
import PackageComparisonAccordion from '../PackageComparisonAccordion';
import Icon from '../Atoms/Icon';
import { INPUT_DATE_FORMAT } from '../../../config/helpers';
import { ACCORDION_ITEM_CLASS, EMPTY_ACCORDION_ITEM_CLASS } from './helpers';

const notApplicableValue = '-';

function getComparisonRows(labels) {
  return [
    // Duration
    {
      label: labels.durationLineItemText,
      getValue: (tour) => {
        const fromDate = new Date(tour.fromDate);
        const toDate = new Date(tour.toDate);
        const diffTime = Math.abs(toDate - fromDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return (
          <div className="ComparisonGrid__tourItem ComparisonGrid__tourItem--firstRow">
            <div className="ComparisonGrid__labelTextMobile">{labels.durationLineItemText}</div>
            <div>{`${diffDays} ${labels.daysText}`}</div>
          </div>
        );
      },
    },
    // Start Date
    {
      label: labels.startDateLineItemText,
      getValue: (tour) => (
        <div className="ComparisonGrid__tourItem">
          <div className="ComparisonGrid__labelTextMobile">{labels.startDateLineItemText}</div>
          <div>{moment(tour.fromDate).format(INPUT_DATE_FORMAT) || ''}</div>
        </div>
      ),
    },
    // End Date
    {
      label: labels.endDateLineItemText,
      getValue: (tour) => (
        <div className="ComparisonGrid__tourItem">
          <div className="ComparisonGrid__labelTextMobile">{labels.endDateLineItemText}</div>
          <div>{moment(tour.toDate).format(INPUT_DATE_FORMAT) || ''}</div>
        </div>
      ),
    },
    // Locations
    {
      label: labels.locationLineItemText,
      getValue: (tour) => {
        if (tour.locations && tour.locations.length > 0) {
          const maxLocationsToShow = 3;
          const displayedLocations = tour.locations.slice(0, maxLocationsToShow).join(', ');
          const remainingCount = tour.locations.length - maxLocationsToShow;
          const moreText = remainingCount > 0 ? ` +${remainingCount}` : '';

          return (
            <div className="ComparisonGrid__tourItem ComparisonGrid__tourItem--tooltip">
              <div className="ComparisonGrid__labelTextMobile">{labels.locationLineItemText}</div>
              <div>
                {displayedLocations}
                {moreText && (
                  <>
                    <span className="ComparisonGrid__moreText">{moreText}</span>
                    <span className="ComparisonGrid__tooltip">
                      <Icon id="tooltip" blockName="ComparisonGrid__tooltipIcon" />
                      <span className="ComparisonGrid__tooltipText">
                        {tour.locations.join(', ')}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        }
        return (
          <div
            className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
            role="presentation"
          >
            <div className="ComparisonGrid__labelTextMobile">{labels.locationLineItemText}</div>
            <div>{notApplicableValue}</div>
          </div>
        );
      },
    },
    // Matches / Tournament
    {
      label: labels.matchTournamentLineItemText,
      getValue: (tour) => {
        const hasMatches = tour.matches?.length > 0;
        if (tour.tournament || hasMatches) {
          const maxMatchesToShow = tour.tournament ? 2 : 3;
          let displayedMatchesTournaments = '';
          let allMatchesTournaments = '';
          if (tour.tournament) {
            displayedMatchesTournaments = hasMatches ? `${tour.tournament}, ` : tour.tournament;
            allMatchesTournaments = displayedMatchesTournaments;
          }
          if (hasMatches) {
            const displayedMatches = tour.matches.slice(0, maxMatchesToShow).join(', ');
            displayedMatchesTournaments = displayedMatchesTournaments.concat(displayedMatches);
            allMatchesTournaments = allMatchesTournaments.concat(tour.matches.join(', '));
          }

          const remainingCount = tour.matches.length - maxMatchesToShow;
          const moreText = remainingCount > 0 ? ` +${remainingCount}` : '';

          return (
            <div className="ComparisonGrid__tourItem ComparisonGrid__tourItem--tooltip">
              <div className="ComparisonGrid__labelTextMobile">
                {labels.matchTournamentLineItemText}
              </div>
              <div>
                {displayedMatchesTournaments}
                {moreText && (
                  <>
                    <span className="ComparisonGrid__moreText">{moreText}</span>
                    <span className="ComparisonGrid__tooltip">
                      <Icon id="tooltip" blockName="ComparisonGrid__tooltipIcon" />
                      <span className="ComparisonGrid__tooltipText">{allMatchesTournaments}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        }
        return (
          <div
            className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
            role="presentation"
          >
            <div className="ComparisonGrid__labelTextMobile">
              {labels.matchTournamentLineItemText}
            </div>
            <div>{notApplicableValue}</div>
          </div>
        );
      },
    },
    // Hotels
    {
      label: labels.hotelLineItemText,
      getValue: (tour, maxLengths, refs, columnIndex, handleAccordionToggle) => {
        const hotelOptions = tour.accommodationOptions?.filter(
          (option) => option.accommodationType === 'hotel'
        );

        const hasUpgrades = hotelOptions.some((option) => option.upgrades?.length > 0);

        const emptyCellsNonAccordion =
          maxLengths.maxLength > hotelOptions.length
            ? [...Array(maxLengths.maxLength - hotelOptions.length)].map((item, index) => ({
                id: index,
              }))
            : [];

        const hotelUpgradesLength = hasUpgrades
          ? hotelOptions.reduce((acc, option) => acc + (option?.upgrades?.length || 0), 0)
          : 0;

        const emptyUpgradeRows = [...Array(maxLengths.maxUpgradesLength - hotelUpgradesLength)].map(
          (item, index) => ({
            id: index,
          })
        );

        return (
          <div
            key={`${labels.hotelLineItemText}-${tour.key}`}
            className="ComparisonGrid__tourItem ComparisonGrid__nestedColumn"
          >
            {hotelOptions?.length > 0 &&
              hotelOptions.map((hotel, index) => {
                const hotelInfo = `${hotel.defaultOption.option.location} - ${hotel.defaultOption.option.title} (${hotel.defaultOption.option.type})`;
                return (
                  <div
                    key={hotel.key}
                    className="ComparisonGrid__tourItem ComparisonGrid__dataCell ComparisonGrid__nestedCell"
                    role="cell"
                  >
                    {index === 0 && (
                      <div className="ComparisonGrid__labelTextMobile">
                        {labels.hotelLineItemText}
                      </div>
                    )}
                    <div>{hotelInfo}</div>
                  </div>
                );
              })}
            {emptyCellsNonAccordion.map((cell) => (
              <div
                className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
                key={`${tour.key}-emptyHotelCell-${cell.id}`}
                role="presentation"
              >
                {notApplicableValue}
              </div>
            ))}
            {/* Accordion for hotel upgrades */}
            {hasUpgrades && (
              <PackageComparisonAccordion
                title={labels.hotelUpgradeAccordionText}
                accordionType={`${labels.hotelUpgradeAccordionText}-nonEmpty`}
                ref={(el) => {
                  if (!refs.current[labels.hotelLineItemText]) {
                    refs.current[labels.hotelLineItemText] = {};
                  }
                  refs.current[labels.hotelLineItemText][
                    `${labels.hotelUpgradeAccordionText}-nonEmpty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) => handleAccordionToggle(labels.hotelLineItemText, isNowOpen)}
              >
                {hotelOptions
                  .flatMap((hotelOption) => hotelOption.upgrades || [])
                  .map((upgrade) => (
                    <div key={upgrade.key} className={ACCORDION_ITEM_CLASS}>
                      {upgrade?.option?.title}
                    </div>
                  ))}
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyHotelUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
            {!hasUpgrades && maxLengths.maxUpgradesLength > 0 && (
              <PackageComparisonAccordion
                title={notApplicableValue}
                accordionType={`${labels.hotelUpgradeAccordionText}-empty`}
                isEmpty
                ref={(el) => {
                  if (!refs.current[labels.hotelLineItemText]) {
                    refs.current[labels.hotelLineItemText] = {};
                  }
                  refs.current[labels.hotelLineItemText][
                    `${labels.hotelUpgradeAccordionText}-empty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) => handleAccordionToggle(labels.hotelLineItemText, isNowOpen)}
              >
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyHotelUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
          </div>
        );
      },
    },
    // Motorhome
    {
      label: labels.motorhomeLineItemText,
      getValue: (tour, maxLengths, refs, columnIndex, handleAccordionToggle) => {
        const motorhomeOptions = tour.accommodationOptions?.filter(
          (option) => option.accommodationType === 'motorhome'
        );
        const hasUpgrades = motorhomeOptions.some((option) => option.upgrades?.length > 0);

        const emptyCellsNonAccordion =
          maxLengths.maxLength > motorhomeOptions.length
            ? [...Array(maxLengths.maxLength - motorhomeOptions.length)].map((item, index) => ({
                id: index,
              }))
            : [];

        const motorhomeUpgradesLength = hasUpgrades
          ? motorhomeOptions.reduce((acc, option) => acc + (option?.upgrades?.length || 0), 0)
          : 0;

        const emptyUpgradeRows = [
          ...Array(maxLengths.maxUpgradesLength - motorhomeUpgradesLength),
        ].map((item, index) => ({
          id: index,
        }));

        return (
          <div
            key={`${labels.motorhomeLineItemText}-${tour.key}`}
            className="ComparisonGrid__tourItem ComparisonGrid__nestedColumn"
          >
            {motorhomeOptions?.length > 0 &&
              motorhomeOptions.map((motorhome, index) => {
                const motorhomeInfo = `${motorhome.defaultOption.option.title} - ${
                  motorhome.defaultOption.option.numberOfBerth
                } ${labels.berthText || 'berth'}`;
                return (
                  <div
                    key={motorhome.key}
                    className="ComparisonGrid__tourItem ComparisonGrid__dataCell ComparisonGrid__nestedCell"
                    role="cell"
                  >
                    {index === 0 && (
                      <div className="ComparisonGrid__labelTextMobile">
                        {labels.motorhomeLineItemText}
                      </div>
                    )}
                    <div>{motorhomeInfo}</div>
                  </div>
                );
              })}
            {emptyCellsNonAccordion.map((cell) => (
              <div
                className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
                key={`${tour.key}-emptyMotorhomeCell-${cell.id}`}
                role="presentation"
              >
                {notApplicableValue}
              </div>
            ))}
            {/* Accordion for motorhome upgrades */}
            {hasUpgrades && (
              <PackageComparisonAccordion
                title={labels.motorhomeUpgradeAccordionText}
                accordionType={`${labels.motorhomeUpgradeAccordionText}-nonEmpty`}
                ref={(el) => {
                  if (!refs.current[labels.motorhomeLineItemText]) {
                    refs.current[labels.motorhomeLineItemText] = {};
                  }
                  refs.current[labels.motorhomeLineItemText][
                    `${labels.motorhomeUpgradeAccordionText}-nonEmpty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.motorhomeLineItemText, isNowOpen)
                }
              >
                {motorhomeOptions
                  .flatMap((option) => option.upgrades || [])
                  .map((upgrade) => (
                    <div key={upgrade.key} className={ACCORDION_ITEM_CLASS}>
                      {upgrade.option.title}
                    </div>
                  ))}
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyMotorhomeUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
            {!hasUpgrades && maxLengths.maxUpgradesLength > 0 && (
              <PackageComparisonAccordion
                title={notApplicableValue}
                accordionType={`${labels.motorhomeUpgradeAccordionText}-empty`}
                isEmpty
                ref={(el) => {
                  if (!refs.current[labels.motorhomeLineItemText]) {
                    refs.current[labels.motorhomeLineItemText] = {};
                  }
                  refs.current[labels.motorhomeLineItemText][
                    `${labels.motorhomeUpgradeAccordionText}-empty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.motorhomeLineItemText, isNowOpen)
                }
              >
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyMotorhomeUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
          </div>
        );
      },
    },
    // Camping
    {
      label: labels.campingLineItemText || 'Camping',
      getValue: (tour, maxLengths, refs, columnIndex, handleAccordionToggle) => {
        // Only show camping options if they exist
        if (!tour.campingOptions || tour.campingOptions.length === 0) {
          return (
            <div className="ComparisonGrid__dataCell ComparisonGrid__tourItem" role="presentation">
              <div className="ComparisonGrid__labelTextMobile">
                {labels.campingLineItemText || 'Camping'}
              </div>
              <div>{notApplicableValue}</div>
            </div>
          );
        }

        const campingOptions = tour.campingOptions;
        const hasUpgrades = campingOptions.some((option) => option.upgrades?.length > 0);

        const emptyCellsNonAccordion =
          maxLengths.maxLength > campingOptions.length
            ? [...Array(maxLengths.maxLength - campingOptions.length)].map((item, index) => ({
                id: index,
              }))
            : [];

        const campingUpgradesLength = hasUpgrades
          ? campingOptions.reduce((acc, option) => acc + (option?.upgrades?.length || 0), 0)
          : 0;

        const emptyUpgradeRows = [
          ...Array(maxLengths.maxUpgradesLength - campingUpgradesLength),
        ].map((item, index) => ({
          id: index,
        }));

        return (
          <div
            key={`${labels.campingLineItemText}-${tour.key}`}
            className="ComparisonGrid__tourItem ComparisonGrid__nestedColumn"
          >
            {campingOptions.map((camping, index) => {
              const campingInfo = `${camping.defaultOption.option.title} - ${
                camping.defaultOption.option.numberOfBerth
              } ${labels.campingOverlayBerthLabelText || 'person'}`;
              return (
                <div
                  key={camping.key}
                  className="ComparisonGrid__tourItem ComparisonGrid__dataCell ComparisonGrid__nestedCell"
                  role="cell"
                >
                  {index === 0 && (
                    <div className="ComparisonGrid__labelTextMobile">
                      {labels.campingLineItemText || 'Camping'}
                    </div>
                  )}
                  <div>{campingInfo}</div>
                </div>
              );
            })}
            {emptyCellsNonAccordion.map((cell) => (
              <div
                className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
                key={`${tour.key}-emptyCampingCell-${cell.id}`}
                role="presentation"
              >
                {notApplicableValue}
              </div>
            ))}
            {/* Accordion for camping upgrades */}
            {hasUpgrades && (
              <PackageComparisonAccordion
                title={labels.campingUpgradeAccordionText || 'Camping Upgrades'}
                accordionType={`${
                  labels.campingUpgradeAccordionText || 'camping-upgrades'
                }-nonEmpty`}
                ref={(el) => {
                  if (!refs.current[labels.campingLineItemText || 'Camping']) {
                    refs.current[labels.campingLineItemText || 'Camping'] = {};
                  }
                  refs.current[labels.campingLineItemText || 'Camping'][
                    `${
                      labels.campingUpgradeAccordionText || 'camping-upgrades'
                    }-nonEmpty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.campingLineItemText || 'Camping', isNowOpen)
                }
              >
                {campingOptions
                  .flatMap((option) => option.upgrades || [])
                  .map((upgrade) => (
                    <div key={upgrade.key} className={ACCORDION_ITEM_CLASS}>
                      {upgrade.option.title}
                    </div>
                  ))}
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyCampingUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
            {!hasUpgrades && maxLengths.maxUpgradesLength > 0 && (
              <PackageComparisonAccordion
                title={notApplicableValue}
                accordionType={`${labels.campingUpgradeAccordionText || 'camping-upgrades'}-empty`}
                isEmpty
                ref={(el) => {
                  if (!refs.current[labels.campingLineItemText || 'Camping']) {
                    refs.current[labels.campingLineItemText || 'Camping'] = {};
                  }
                  refs.current[labels.campingLineItemText || 'Camping'][
                    `${
                      labels.campingUpgradeAccordionText || 'camping-upgrades'
                    }-empty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.campingLineItemText || 'Camping', isNowOpen)
                }
              >
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyCampingUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
          </div>
        );
      },
    },
    // Flight
    {
      label: labels.flightLineItemText,
      getValue: (tour) => {
        const flightOption = tour.flightOptions?.[0]?.defaultOption?.option;
        if (flightOption) {
          const hasFlightCodes =
            flightOption?.outboundDepartureAirportCode && flightOption?.outboundArrivalAirportCode;
          return (
            <div>
              <div className="ComparisonGrid__tourItem">
                <div className="ComparisonGrid__labelTextMobile">{labels.flightLineItemText}</div>
                <div>
                  {hasFlightCodes
                    ? `${flightOption.outboundDepartureAirportCode} - ${flightOption.outboundArrivalAirportCode}`
                    : flightOption?.title}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="ComparisonGrid__dataCell ComparisonGrid__tourItem" role="presentation">
            <div className="ComparisonGrid__labelTextMobile">{labels.flightLineItemText}</div>
            <div>{notApplicableValue}</div>
          </div>
        );
      },
    },
    // Cabin
    {
      label: labels.flightClassLineItemText,
      getValue: (tour, maxLengths, refs, columnIndex, handleAccordionToggle) => {
        const flightClass =
          tour.flightOptions?.[0]?.defaultOption?.flightClasses?.[0]?.option.flightClass;
        const flightUpgradesLength = tour.flightOptions?.[0]?.upgrades?.length || 0;
        const emptyUpgradeRows =
          maxLengths.maxUpgradesLength > flightUpgradesLength
            ? [...Array(maxLengths.maxUpgradesLength - flightUpgradesLength)].map(
                (item, index) => ({
                  id: index,
                })
              )
            : [];

        return (
          <div>
            {/* Default Flight Classes */}
            {flightClass ? (
              <div key={flightClass.key} className="ComparisonGrid__tourItem">
                <div className="ComparisonGrid__labelTextMobile">
                  {labels.flightClassLineItemText}
                </div>
                <div>{flightClass}</div>
              </div>
            ) : (
              <div
                className="ComparisonGrid__dataCell ComparisonGrid__tourItem"
                role="presentation"
              >
                <div className="ComparisonGrid__labelTextMobile">
                  {labels.flightClassLineItemText}
                </div>
                <div>{notApplicableValue}</div>
              </div>
            )}
            {/* Accordion for flight upgrades */}
            {flightUpgradesLength > 0 && (
              <PackageComparisonAccordion
                title={labels.flightUpgradeAccordionText}
                accordionType={`${labels.flightUpgradeAccordionText}-nonEmpty`}
                ref={(el) => {
                  if (!refs.current[labels.flightClassLineItemText]) {
                    refs.current[labels.flightClassLineItemText] = {};
                  }
                  refs.current[labels.flightClassLineItemText][
                    `${labels.flightUpgradeAccordionText}-nonEmpty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.flightClassLineItemText, isNowOpen)
                }
              >
                {tour.flightOptions[0].upgrades.map((upgrade) => {
                  const hasFlightCodes =
                    upgrade?.option?.outboundDepartureAirportCode &&
                    upgrade?.option?.outboundArrivalAirportCode;
                  return (
                    <div key={upgrade.key} className={ACCORDION_ITEM_CLASS}>
                      <div>
                        {' '}
                        {hasFlightCodes
                          ? `${upgrade.option.outboundDepartureAirportCode} - ${upgrade.option.outboundArrivalAirportCode}`
                          : upgrade?.option?.title}
                      </div>
                      <div>{upgrade?.flightClasses?.[0]?.option.flightClass}</div>
                    </div>
                  );
                })}
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyFlightUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
            {flightUpgradesLength === 0 && maxLengths.maxUpgradesLength > 0 && (
              <PackageComparisonAccordion
                title={notApplicableValue}
                accordionType={`${labels.flightUpgradeAccordionText}-empty`}
                isEmpty
                ref={(el) => {
                  if (!refs.current[labels.flightClassLineItemText]) {
                    refs.current[labels.flightClassLineItemText] = [];
                  }
                  refs.current[labels.flightClassLineItemText][
                    `${labels.flightUpgradeAccordionText}-empty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.flightClassLineItemText, isNowOpen)
                }
              >
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyFlightUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
          </div>
        );
      },
    },
    // Stopover
    {
      label: labels.stopoverLineItemText,
      getValue: (tour) => (
        // Assuming stopoverAvailableText and stopoverNotAvailableText are labels provided
        <div className="ComparisonGrid__tourItem">
          <div className="ComparisonGrid__labelTextMobile">{labels.stopoverLineItemText}</div>
          <div>
            {tour.hasStopovers
              ? labels.stopoverAvailableText || 'Available'
              : labels.stopoverNotAvailableText || 'Not Available'}
          </div>
        </div>
      ),
    },
    // Tickets
    {
      label: labels.ticketLineItemText,
      getValue: (tour, maxLengths, refs, columnIndex, handleAccordionToggle) => {
        const defaultTicketOption = tour.ticketOptions?.[0]?.defaultOption?.option;
        const ticketUpgrades = tour.ticketOptions?.[0]?.upgrades;

        const emptyUpgradeRows =
          maxLengths.maxUpgradesLength > ticketUpgrades.length
            ? [...Array(maxLengths.maxUpgradesLength - ticketUpgrades.length)].map(
                (item, index) => ({
                  id: index,
                })
              )
            : [];

        return (
          <div>
            {defaultTicketOption ? (
              <div className="ComparisonGrid__tourItem">
                <div className="ComparisonGrid__labelTextMobile">{labels.ticketLineItemText}</div>
                <div>{defaultTicketOption.title}</div>
              </div>
            ) : (
              <div
                className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
                role="presentation"
              >
                <div className="ComparisonGrid__labelTextMobile">{labels.ticketLineItemText}</div>
                <div>{notApplicableValue}</div>
              </div>
            )}
            {/* Accordion for ticket upgrades */}
            {ticketUpgrades && ticketUpgrades.length > 0 && (
              <PackageComparisonAccordion
                title={labels.ticketUpgradeAccordionText}
                accordionType={`${labels.ticketUpgradeAccordionText}-nonEmpty`}
                ref={(el) => {
                  if (!refs.current[labels.ticketLineItemText]) {
                    refs.current[labels.ticketLineItemText] = {};
                  }
                  refs.current[labels.ticketLineItemText][
                    `${labels.ticketUpgradeAccordionText}-nonEmpty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.ticketLineItemText, isNowOpen)
                }
              >
                {ticketUpgrades.map((upgrade) => (
                  <div key={upgrade.key} className={ACCORDION_ITEM_CLASS}>
                    {upgrade.option.title}
                  </div>
                ))}
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyTicketUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
            {ticketUpgrades.length === 0 && maxLengths.maxUpgradesLength > 0 && (
              <PackageComparisonAccordion
                title={notApplicableValue}
                accordionType={`${labels.ticketUpgradeAccordionText}-empty`}
                isEmpty
                ref={(el) => {
                  if (!refs.current[labels.ticketLineItemText]) {
                    refs.current[labels.ticketLineItemText] = [];
                  }
                  refs.current[labels.ticketLineItemText][
                    `${labels.ticketUpgradeAccordionText}-empty-${columnIndex}`
                  ] = el;
                }}
                onToggle={(isNowOpen) =>
                  handleAccordionToggle(labels.ticketLineItemText, isNowOpen)
                }
              >
                {emptyUpgradeRows.map((upgradeRow) => (
                  <div
                    key={`${tour.key}-emptyTicketUpgrade-${upgradeRow.id}`}
                    className={EMPTY_ACCORDION_ITEM_CLASS}
                  >
                    {notApplicableValue}
                  </div>
                ))}
              </PackageComparisonAccordion>
            )}
          </div>
        );
      },
    },
    // Inclusions
    {
      label: labels.extraLineItemText || 'Inclusions',
      getValue: (tour, maxLengths) => {
        const inclusions = tour.extraOptions; // extras are inclusions
        const emptyCells =
          maxLengths.maxLength > inclusions.length
            ? [...Array(maxLengths.maxLength - inclusions.length)].map((item, index) => ({
                id: index,
              }))
            : [];
        if (inclusions && inclusions.length > 0) {
          return (
            <div
              key={`${labels.extraLineItemText}-${tour.key}`}
              className="ComparisonGrid__tourItem ComparisonGrid__nestedColumn"
            >
              {inclusions.map((inclusion, index) => (
                <div
                  key={inclusion}
                  className="ComparisonGrid__tourItem ComparisonGrid__dataCell ComparisonGrid__nestedCell"
                  role="cell"
                >
                  {index === 0 && (
                    <div className="ComparisonGrid__labelTextMobile">
                      {labels.extraLineItemText}
                    </div>
                  )}
                  <div>{inclusion?.title}</div>
                </div>
              ))}
              {emptyCells.map((cell) => (
                <div
                  className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
                  key={`${tour.key}-emptyInclusionsCell-${cell.id}`}
                  role="presentation"
                >
                  {notApplicableValue}
                </div>
              ))}
            </div>
          );
        }
        return emptyCells.map((cell, index) => (
          <div
            className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell"
            key={`${tour.key}-emptyInclusionsCell-${cell.id}`}
            role="presentation"
          >
            {index === 0 && (
              <div className="ComparisonGrid__labelTextMobile">{labels.extraLineItemText}</div>
            )}
            <div>{notApplicableValue}</div>
          </div>
        ));
      },
    },
    // Merchandise
    {
      label: labels.merchandiseLineItemText || 'Merchandise',
      getValue: (tour) => (
        <div className="ComparisonGrid__tourItem">
          <div className="ComparisonGrid__labelTextMobile">{labels.merchandiseLineItemText}</div>
          <div>
            {tour.hasMerchandise
              ? labels.tourComponentAvailableText || 'Available'
              : labels.tourComponentNotAvailableText || 'Not Available'}
          </div>
        </div>
      ),
    },
    // Extras (not included in the price)
    {
      label: labels.excursionLineItemText || 'Extras',
      getValue: (tour, maxLengths) => {
        const extraOptions = tour.excursionOptions; // excursions are extras

        const emptyCells =
          maxLengths.maxLength > extraOptions.length
            ? [...Array(maxLengths.maxLength - extraOptions.length)].map((item, index) => ({
                id: index,
              }))
            : [];

        if (extraOptions && extraOptions.length > 0) {
          return (
            <div
              key={`${labels.excursionLineItemText}-${tour.key}`}
              className="ComparisonGrid__nestedColumn"
            >
              {extraOptions.map((option, index) => (
                <div
                  key={option.key}
                  className="ComparisonGrid__tourItem ComparisonGrid__dataCell ComparisonGrid__nestedCell--lastRow"
                  role="cell"
                >
                  {index === 0 && (
                    <div className="ComparisonGrid__labelTextMobile">
                      {labels.excursionLineItemText}
                    </div>
                  )}
                  <div>{option?.subTitle}</div>
                </div>
              ))}
              {emptyCells.map((cell) => (
                <div
                  className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell--lastRow"
                  key={`${tour.key}-emptyExtrasCell-${cell.id}`}
                  role="presentation"
                >
                  {notApplicableValue}
                </div>
              ))}
            </div>
          );
        }
        return emptyCells.map((cell, index) => (
          <div
            className="ComparisonGrid__dataCell ComparisonGrid__tourItem ComparisonGrid__nestedCell--lastRow"
            key={`${tour.key}-emptyExtrasCell-${cell.id}`}
            role="presentation"
          >
            {index === 0 && (
              <div className="ComparisonGrid__labelTextMobile">{labels.excursionLineItemText}</div>
            )}
            <div>{notApplicableValue}</div>
          </div>
        ));
      },
    },
  ];
}

export default getComparisonRows;
